import React, { useEffect, useRef } from "react";

const Why_WpMarketing = () => {
    const rootRef = useRef(null);

    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        let started = false, io = null;

        const begin = () => {
            if (started) return; started = true;
            io = new IntersectionObserver(([e]) => {
                if (e.isIntersecting) { el.classList.add("is-in"); io?.disconnect(); }
            }, { threshold: 0.25, rootMargin: "0px 0px -10% 0px" });
            io.observe(el);
        };
        if (window.scrollY > 0) begin();
        else window.addEventListener("scroll", begin, { once: true, passive: true });

        return () => { window.removeEventListener("scroll", begin); io?.disconnect(); };
    }, []);

    const items = [
        {
            icon: "📈",
            title: "High Engagement & Conversions",
            desc: "98% open rate ensures your messages get seen and drive quick results.",
            bestFor: "Lead gen, instant support",
            kpi: "CTR ↑, CPA ↓",
        },
        {
            icon: "👥",
            title: "Personalized Customer Communication",
            desc: "Send real-time offers and updates in your customer’s language.",
            bestFor: "Product discovery",
            kpi: "View→Cart ↑",
        },
        {
            icon: "🛍️",
            title: "Catalogs & Rich Media Messaging",
            desc: "Showcase products with images, videos, and catalogs right in chat.",
            bestFor: "Conversions",
            kpi: "Checkout rate ↑",
        },
        {
            icon: "💳",
            title: "In-Chat Payments",
            desc: "Enable UPI and wallet checkout inside WhatsApp with instant confirmation.",
            bestFor: "Proactive outreach",
            kpi: "Open rate ↑",
        },
        {
            icon: "🤖",
            title: "Automation with Chatbots",
            desc: "Answer FAQs, track orders, and capture leads 24×7 automatically.",
            bestFor: "Retention",
            kpi: "Revenue/chat ↑",
        },
        {
            icon: "📊",
            title: "Analytics & Optimization",
            desc: "Track clicks & conversions and test campaigns for better results.",
            bestFor: "Optimization",
            kpi: "Conv rate ↑",
        },
    ];

    return (
        <section className="py-5 position-relative" style={{ overflow: "hidden" }}>
            <div
                ref={rootRef}
                className="wwm-reveal px-5 x-xxl-5 px-6 mx-auto position-relative"
                style={{ maxWidth: "76%", zIndex: 1 }}
            >
                {/* heading */}
                <div style={{ marginTop: "50px", marginBottom: 40 }}>
                    <h2
                        style={{
                            fontSize: "2.5rem",
                            fontFamily: "Inter",
                            fontWeight: 700,
                            color: "#242121ff",
                            lineHeight: 1.2,
                        }}
                    >
                        Why Choose <span style={{ color: "#1DB954" }}>WhatsApp</span> Marketing?
                    </h2>
                    <p
                        style={{
                            marginTop: -2,
                            color: "#413e3eff",
                            fontSize: "1.1rem",
                            fontFamily: "Poppins",
                            fontWeight: 300,
                            lineHeight: 1.2,
                        }}
                    >
                        WhatsApp offers direct, personal and instant engagement with the highest open rates in marketing. Every message becomes a touchpoint to engage, nurture and convert.
                    </p>
                </div>

                {/* 🔹 Cards Grid */}
                <div className="wm-grid">
                    {items.map((c, i) => (
                        <article className="wm-card" key={i}>
                            <div className="wm-badge">{c.icon}</div>

                            <h3 className="wm-title">{c.title}</h3>
                            <p className="wm-desc">{c.desc}</p>

                            <div className="wm-chiprow">
                                <span className="wm-chip">{c.bestFor}</span>
                                <span className="wm-chip kpi">{c.kpi}</span>
                            </div>

                            <button className="wm-cta">See examples</button>
                            <div className="wm-glow" />
                        </article>
                    ))}
                </div>
            </div>

            <style>{`
        .wwm-reveal{ opacity:0; transform:translateY(-12px);
          transition:opacity .6s ease, transform .6s ease; }
        .wwm-reveal.is-in{ opacity:1; transform:none; }

        .wm-grid{ display:grid; gap:24px; grid-template-columns:repeat(12,1fr); }
        .wm-card{ grid-column:span 4; }
        @media (max-width:1024px){ .wm-grid{grid-template-columns:repeat(8,1fr)} .wm-card{grid-column:span 4} }
        @media (max-width:640px){ .wm-grid{grid-template-columns:repeat(4,1fr)} .wm-card{grid-column:span 4} }

        .wm-card{
          position:relative; isolation:isolate;
          background:#fff;
          border:1px solid #e8edf7;
          border-radius:18px;
          padding:18px 18px 16px;
          box-shadow:0 10px 28px rgba(13,39,80,.06);
          transition:transform .25s ease, box-shadow .25s ease, border-color .25s ease;
        }
        .wm-card:hover{
          transform:translateY(-3px);
          box-shadow:0 18px 46px rgba(13,39,80,.12);
          border-color:#d7e7ff;
        }

        .wm-badge{
          width:48px; height:48px; border-radius:12px;
          display:grid; place-items:center; font-size:22px;
          background:linear-gradient(180deg,#EFFFF6,#EDF6FF);
          border:1px solid #d8e8ff;
          box-shadow:0 8px 18px rgba(18,86,150,.08);
          margin-bottom:10px;
        }

        .wm-title{ 
            margin:0 0 6px 0; 
            color: #332f2fff; 
            font-size: 20px;
            font-family: Inter;
            Font-weight: 600
         }
        .wm-desc{
            margin:0 0 14px 0; 
            color: #5a6776; 
            font-size: 16px;
            font-family: Lato;
            font-weight: 400;
            line-height: 1.2
        }

        .wm-chiprow{ 
            display:flex; 
            gap:10px; 
            flex-wrap:wrap; 
            margin-bottom:14px; 
        }

        .wm-chip{
            display:inline-flex; 
            align-items:center; 
            gap:6px;
            padding:7px 10px; 
            border-radius: 18px;
            background: #e8eefdff; 
            color: #264785; 
            font-size: 10px,
            font-family: Lato;
            font-weight: 400;
        }
        .wm-chip.kpi{ 
             background: #def9edff; 
             color: #0f5a4c;
             border-radius: 18px;
             font-size: 10px,
             font-family: Lato;
             font-weight: 400;
         }

        .wm-cta{
          padding:10px 14px;
          border-radius:10px;
          background:#fff;
          border:1px solid #e8f0feff;
          font-weight:700;
          cursor:pointer;
        }
          
        .wm-cta:hover{ 
           border-color: #cfe0ff; 
        }

        .wm-card:hover .wm-glow{ opacity:1; transform:scale(1); }
        .wm-glow{
          position:absolute; 
          inset:-1px; 
          border-radius:18px;
          background:conic-gradient(from 0deg,#24E3D6,#09A7FF,#24E3D6);
          -webkit-mask:linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite:xor; mask-composite:exclude;
          opacity:0; transform:scale(.985);
          transition:opacity .3s ease, transform .3s ease;
        }
      `}</style>
        </section>
    );
};

export default Why_WpMarketing;




